import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { HMSRoomProvider, useHMSStore, selectPeerCount } from '@100mslive/react-sdk';
import { Package, AlertCircle, Share2, FileText, Eye, Wallet, ShoppingBag, Volume2, VolumeX, CreditCard, Check } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { Img } from './Img';
import { VideoViewer } from './VideoViewer';
import { ShareModal } from './ShareModal';
import { Chat } from './Chat';
import { ReadOnlyChat } from './ReadOnlyChat';
import { Header } from './Header';
import { RetryPaymentModal } from './RetryPaymentModal';
import { AuthPromptModal } from './AuthPromptModal';
import { SellerMenu } from './SellerMenu';
import { NotesModal } from './NotesModal';
import { WalletModal } from './WalletModal';
import { CustomBidModal } from './CustomBidModal';
import { PurchasesModal } from './PurchasesModal';
import AuctionWinOverlay from './AuctionWinOverlay';
import { useBuyerWebSocket } from '../hooks/useBuyerWebSocket';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getShow, getHmsToken, getPublicHmsViewerToken, getAddresses, getMyShowTotals, getSalesByBuyer, getPublicProfile, getBalanceSummary, setBalanceAutoApply, SITE_URL, type ShowBuyerTotals } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { useVerification } from '../App';
import { calculateShippingEstimate, formatShippingCost } from '../utils/shippingCalculator';
import type { Show, BuyerAddress, ItemSaleWithPayment } from '../types';
import { colors } from '../constants/colors';
import './BuyerView.css';

function getBidIncrement(currentPrice: number): number {
  if (currentPrice < 25) return 1;
  if (currentPrice < 100) return 5;
  if (currentPrice < 500) return 10;
  return 25;
}

export function BuyerView() {
  const { showId } = useParams<{ showId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const { showVerification } = useVerification();
  const [show, setShow] = useState<Show | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [sideMenuOpen, setSideMenuOpen] = useState<null | 'notes' | 'wallet' | 'purchases'>(null);
  const [showCustomBidModal, setShowCustomBidModal] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [defaultAddress, setDefaultAddress] = useState<BuyerAddress | null>(null);
  const [hasCheckedAddress, setHasCheckedAddress] = useState(false);
  const [buyerTotals, setBuyerTotals] = useState<ShowBuyerTotals | null>(null);
  const [failedSale, setFailedSale] = useState<{ id: string; itemName: string; amount: number } | null>(null);
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [highBidderAvatarUrl, setHighBidderAvatarUrl] = useState<string | null>(null);
  const [winnerAvatarUrl, setWinnerAvatarUrl] = useState<string | null>(null);
  const [celebration, setCelebration] = useState<{ username: string; winnerId: string } | null>(null);
  const [lastCelebratedAuctionId, setLastCelebratedAuctionId] = useState<string | null>(null);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [useBalance, setUseBalance] = useState(user?.useBalanceForPurchases !== false);
  const [outbidShake, setOutbidShake] = useState(false);
  const prevHighBidderIdRef = useRef<string | null>(null);

  const handleUseBalanceChange = (value: boolean) => {
    setUseBalance(value);
    setBalanceAutoApply(value).then(() => refreshUser().catch(() => {})).catch(() => {});
  };
  useDocumentTitle(show?.name ?? 'Watching');

  // Toggle mute by directly controlling the HMS audio element.
  // Must run inside the click handler (user gesture) so the browser allows play().
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      const audioEl = document.querySelector<HTMLAudioElement>('audio');
      if (audioEl) {
        audioEl.volume = next ? 0 : 0.8;
        if (!next && audioEl.paused) {
          audioEl.play().catch(() => {});
        }
      }
      return next;
    });
  }, []);

  const {
    isConnected, hasReceivedState, viewerCount, onDeckItem,
    activeAuction, activeBinSale, recentBids, auctionEnded,
    binSaleEnded, showEnded, bidStatus, bidRejectionReason,
    binPurchaseStatus, binPurchaseFailReason, shippingConfig,
    buyerValidation, connect, disconnect, placeBid, purchaseBin, refreshValidation,
    userMaxBidAmount, lastBidFlash,
    broadcasterPaused,
  } = useBuyerWebSocket();

  // Force background behind status bar/notch safe area on mobile (both auth + unauth BuyerView)
  // Black during live show, gray when show ends.
  // Uses matchMedia listener so it reacts to device rotation / browser resize.
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const themeMeta = document.createElement('meta');
    themeMeta.name = 'theme-color';

    const isLive = !showEnded && !error;
    const bgColor = isLive ? '#000' : '#F4F3F8';
    themeMeta.content = bgColor;

    const apply = (isMobile: boolean) => {
      if (isMobile) {
        document.documentElement.classList.toggle('bg-black', isLive);
        document.body.style.setProperty('background-color', bgColor, 'important');
        if (!themeMeta.isConnected) document.head.appendChild(themeMeta);
      } else {
        document.documentElement.classList.remove('bg-black');
        document.body.style.removeProperty('background-color');
        if (themeMeta.isConnected) themeMeta.remove();
      }
    };

    apply(mql.matches);
    const handler = (e: MediaQueryListEvent) => apply(e.matches);
    mql.addEventListener('change', handler);

    return () => {
      mql.removeEventListener('change', handler);
      document.documentElement.classList.remove('bg-black');
      document.body.style.removeProperty('background-color');
      if (themeMeta.isConnected) themeMeta.remove();
    };
  }, [showEnded, error]);

  // Fetch high bidder avatar
  useEffect(() => {
    const username = activeAuction?.highestBidderUsername;
    if (!username || activeAuction?.highestBidderId === user?.id) {
      setHighBidderAvatarUrl(null);
      return;
    }
    getPublicProfile(username)
      .then((profile) => setHighBidderAvatarUrl(profile.avatarUrl || null))
      .catch(() => {});
  }, [activeAuction?.highestBidderUsername, activeAuction?.highestBidderId, user?.id]);

  // Fetch winner avatar
  useEffect(() => {
    const username = auctionEnded?.winnerUsername;
    if (!username || !auctionEnded?.winnerId || auctionEnded.winnerId === user?.id) {
      setWinnerAvatarUrl(null);
      return;
    }
    getPublicProfile(username)
      .then((profile) => setWinnerAvatarUrl(profile.avatarUrl || null))
      .catch(() => {});
  }, [auctionEnded?.winnerUsername, auctionEnded?.winnerId, user?.id]);

  // Trigger the celebration overlay when an auction ends with a winner.
  // Dedupe per (winnerId, winningBid) so we only fire once per auction —
  // auctionEnded is reset to null between auctions, so this transitions
  // from null → winner cleanly.
  //
  // We only store the winner's identity here — the avatarUrl is derived
  // at render time so it stays in sync with `winnerAvatarUrl` (which is
  // fetched async via getPublicProfile and may not be ready yet when
  // this effect first fires).
  useEffect(() => {
    if (!auctionEnded?.winnerId || !auctionEnded.winnerUsername) return;
    const auctionKey = `${auctionEnded.winnerId}:${auctionEnded.winningBid ?? ''}`;
    if (auctionKey === lastCelebratedAuctionId) return;
    setLastCelebratedAuctionId(auctionKey);

    setCelebration({
      username: auctionEnded.winnerUsername,
      winnerId: auctionEnded.winnerId,
    });
  }, [auctionEnded?.winnerId, auctionEnded?.winnerUsername, auctionEnded?.winningBid, lastCelebratedAuctionId]);

  // ---- Data loading (unchanged) ----
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const addresses = await getAddresses();
        setDefaultAddress(addresses.find(a => a.isDefault) || addresses[0] || null);
      } catch {} finally { setHasCheckedAddress(true); }
    })();
    getBalanceSummary().then(b => setAvailableBalance(b.availableBalance)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user || !showId) return;
    getMyShowTotals(showId).then(setBuyerTotals).catch(() => {});
  }, [user, showId]);

  useEffect(() => {
    if (!user || !buyerValidation.hasFailedPayment) { setFailedSale(null); return; }
    getSalesByBuyer().then(sales => {
      const failed = sales.find((s: ItemSaleWithPayment) => s.paymentStatus === 'FAILED');
      if (failed) setFailedSale({ id: failed.id, itemName: failed.itemSnapshot?.name ?? 'Unknown Item', amount: failed.total });
    }).catch(() => {});
  }, [user, buyerValidation.hasFailedPayment]);

  useEffect(() => {
    if (!user || !showId || !auctionEnded || auctionEnded.winnerId !== user.id) return;
    const t1 = setTimeout(() => { getMyShowTotals(showId).then(setBuyerTotals).catch(() => {}); }, 2000);
    const t2 = setTimeout(() => refreshValidation(), 5000);
    // Refetch balance after winning (balance hold reduces available)
    getBalanceSummary().then(b => setAvailableBalance(b.availableBalance)).catch(() => {});
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [user, showId, auctionEnded, refreshValidation]);

  useEffect(() => {
    if (binPurchaseStatus !== 'success') return;
    const t = setTimeout(() => refreshValidation(), 5000);
    // Refetch balance after BIN purchase
    getBalanceSummary().then(b => setAvailableBalance(b.availableBalance)).catch(() => {});
    return () => clearTimeout(t);
  }, [binPurchaseStatus, refreshValidation]);

  // Outbid detection: when highestBidderId changes FROM user.id TO someone else
  useEffect(() => {
    const currentHighBidder = activeAuction?.highestBidderId ?? null;
    const prev = prevHighBidderIdRef.current;
    prevHighBidderIdRef.current = currentHighBidder;
    if (prev === user?.id && currentHighBidder && currentHighBidder !== user?.id) {
      setOutbidShake(true);
      const t = setTimeout(() => setOutbidShake(false), 700);
      return () => clearTimeout(t);
    }
  }, [activeAuction?.highestBidderId, user?.id]);

  useEffect(() => {
    if (!showId) return;
    (async () => {
      try {
        const showData = await getShow(showId);
        setShow(showData);
        if (!isAuthenticated) { setShowAuthModal(true); setLoading(false); return; }
        if (user && showData.sellerId === user.id) { setLoading(false); return; }
        if (showData.status !== 'LIVE') { setError('This show is not currently live'); setLoading(false); return; }
        connect(showId);
        if (showData.hmsRoomId) {
          try { const t = await getHmsToken(showId); setAuthToken(t.authToken); } catch {}
        }
        setLoading(false);
      } catch { setError('Failed to load show'); setLoading(false); }
    })();
    return () => { disconnect(); };
  }, [showId, user, isAuthenticated, connect, disconnect]);

  useEffect(() => {
    const handleFocus = () => {
      if (isConnected && (buyerValidation.missingPaymentMethod || buyerValidation.missingAddress || buyerValidation.hasFailedPayment)) {
        refreshValidation();
        if (user) getAddresses().then(addrs => setDefaultAddress(addrs.find(a => a.isDefault) || addrs[0] || null)).catch(() => {});
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isConnected, buyerValidation.missingPaymentMethod, buyerValidation.missingAddress, buyerValidation.hasFailedPayment, refreshValidation, user]);

  const shareUrl = `${SITE_URL}/watch/${showId}`;
  const shareTitle = `Watch ${show?.sellerUsername} live on Snagg!`;
  const handleAuthenticated = () => { setShowAuthModal(false); window.location.reload(); };
  const handlePlaceBid = () => { if (activeAuction) placeBid(activeAuction.id, 'AUTO_INCREMENT'); };
  const handleCustomBid = () => setShowCustomBidModal(true);
  const handleCustomBidSubmit = (amount: number, bidType: 'CUSTOM' | 'MAX_BID' = 'CUSTOM') => {
    if (!activeAuction) return;
    placeBid(activeAuction.id, bidType, amount);
  };
  const handleBuyNow = () => { if (activeBinSale) purchaseBin(activeBinSale.id); };

  const isSeller = show && user && show.sellerId === user.id;

  // ---- Early return states ----
  if (loading || (!hasReceivedState && !isSeller && isAuthenticated)) {
    return <div style={s.container}><Header /><p style={s.loadingText}>Joining show...</p></div>;
  }
  if (error || showEnded) {
    return (
      <div style={s.container}><Header />
        <div style={s.centeredState}>
          <p style={s.stateText}>{showEnded ? 'This show has ended' : error}</p>
          <button onClick={() => navigate('/')} style={s.grayBtn}>Back to Shows</button>
        </div>
      </div>
    );
  }
  if (show && user && show.sellerId === user.id) {
    return (
      <div style={s.container}><Header />
        <div style={s.centeredState}>
          <p style={s.stateText}>You can't watch your own show as a viewer</p>
          <p style={{ color: '#666', marginBottom: 16 }}>Use the Seller View to manage your live show.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => navigate(`/sell/live/${showId}`)} style={s.primaryBtn}>Go to Seller View</button>
            <button onClick={() => navigate('/')} style={s.grayBtn}>Back to Shows</button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Unauth preview ----
  if (!isAuthenticated && show) {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    return (
      <HMSRoomProvider>
        <UnauthPreview
          show={show}
          showId={showId!}
          isMobile={isMobile}
          shareUrl={shareUrl}
          shareTitle={shareTitle}
          showShareModal={showShareModal}
          setShowShareModal={setShowShareModal}
          showAuthModal={showAuthModal}
          setShowAuthModal={setShowAuthModal}
          onAuthenticated={handleAuthenticated}
        />
      </HMSRoomProvider>
    );
  }

  // ---- Computed values ----
  const currentItem = activeAuction ? { name: onDeckItem?.name || 'Current Item', description: onDeckItem?.description || '', imageUrl: onDeckItem?.imageUrl, startingPrice: onDeckItem?.startingPrice } : onDeckItem;
  const nextBidAmount = activeAuction ? (activeAuction.bidCount === 0 ? Math.ceil(activeAuction.currentPrice) : Math.ceil(activeAuction.currentPrice) + getBidIncrement(activeAuction.currentPrice)) : 0;
  const highBidder = recentBids.length > 0 ? recentBids[0] : null;
  const shippingEstimate = shippingConfig && defaultAddress && onDeckItem ? calculateShippingEstimate(shippingConfig, defaultAddress.zipCode, onDeckItem.weightOz || 16, buyerTotals, activeAuction?.currentPrice || onDeckItem?.startingPrice || 0) : null;
  const binShippingEstimate = shippingConfig && defaultAddress && activeBinSale?.item ? calculateShippingEstimate(shippingConfig, defaultAddress.zipCode, activeBinSale.item.weightOz || 16, buyerTotals, activeBinSale.finalPrice) : null;
  const canBid = buyerValidation.canBid && hasCheckedAddress && defaultAddress !== null;
  const isMissingAddressRejection = bidRejectionReason?.toLowerCase().includes('address');
  const isMissingPaymentMethodRejection = bidRejectionReason?.toLowerCase().includes('payment');

  const bannerCount = [
    buyerValidation.hasFailedPayment,
    buyerValidation.missingPaymentMethod,
    (hasCheckedAddress && !defaultAddress) || buyerValidation.missingAddress,
  ].filter(Boolean).length;

  // ---- Reusable sub-renders ----
  const renderBanners = () => (
    <div className="bv-banners" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {buyerValidation.hasFailedPayment && (
        <div className="bv-banner bv-banner-warn">
          <AlertCircle size={16} /> <span>Failed payment — fix it to continue bidding.</span>
          <button className="bv-banner-action danger" onClick={() => setShowRetryModal(true)}>Retry</button>
        </div>
      )}
      {buyerValidation.missingPaymentMethod && (
        <div className="bv-banner bv-banner-error">
          <AlertCircle size={16} /> <span>Add a payment method to bid</span>
          <Link to="/payment-methods" className="bv-banner-action">Add Card</Link>
        </div>
      )}
      {((hasCheckedAddress && !defaultAddress) || buyerValidation.missingAddress) && (
        <div className="bv-banner bv-banner-warn">
          <AlertCircle size={16} /> <span>Add a shipping address to bid</span>
          <Link to="/addresses" className="bv-banner-action">Add Address</Link>
        </div>
      )}
    </div>
  );

  const renderItemCard = (item: any, meta?: { auction?: typeof activeAuction; ended?: typeof auctionEnded; estimate?: typeof shippingEstimate }) => {
    const isShrink = !!meta?.ended;

    // Status strip content
    let statusStrip: React.ReactNode = null;
    if (meta?.auction?.highestBidderId) {
      if (meta.auction.highestBidderId === user?.id) {
        statusStrip = (
          <div style={{ position: 'relative' }}>
            {lastBidFlash && (
              <div key={`${lastBidFlash.username}-${lastBidFlash.amount}`} className="bv-bid-flash" style={s.bidFlash}>
                <b>{lastBidFlash.username}</b> bid ${Math.ceil(lastBidFlash.amount)}
              </div>
            )}
            <div style={{ ...s.statusStrip, color: '#16A34A' }}>
              <span style={{ ...s.statusDot, background: '#16A34A' }} />
              <span>You're winning</span>
            </div>
          </div>
        );
      } else if (outbidShake) {
        statusStrip = (
          <div style={{ ...s.statusStrip, color: colors.accent.pink }}>
            <span className="bv-pulse-dot" style={{ ...s.statusDot, background: colors.accent.pink }} />
            <b>You've been outbid</b>
          </div>
        );
      } else {
        statusStrip = (
          <div style={{ position: 'relative' }}>
            {lastBidFlash && (
              <div key={`${lastBidFlash.username}-${lastBidFlash.amount}`} className="bv-bid-flash" style={s.bidFlash}>
                <b>{lastBidFlash.username}</b> bid ${Math.ceil(lastBidFlash.amount)}
              </div>
            )}
            <div style={{ ...s.statusStrip, color: '#1d4ed8' }}>
              <UserAvatar src={highBidderAvatarUrl} size={16} alt={highBidder?.bidderName || 'Bidder'} />
              <b>{highBidder?.bidderName || 'Bidder'}</b>
              <span style={{ fontWeight: 500, opacity: 0.85 }}>winning</span>
            </div>
          </div>
        );
      }
    }

    return (
      <div className={outbidShake ? 'bv-outbid-flash' : ''}>
        <div className={outbidShake ? 'bv-outbid-shake' : ''}>
          <div style={isShrink ? s.cardShrink : s.card}>
            <div style={s.cardRow}>
              {/* Thumbnail with item number badge */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                {item?.imageUrl
                  ? <Img src={item.imageUrl} alt={item.name} variant="thumb" renderedWidth={64} blurhash={item.blurhash} style={s.cardImg} />
                  : <div style={s.cardImgPlaceholder}><Package size={20} color="#aaa" /></div>}
                {item?.itemNumber != null && (
                  <span style={s.thumbBadge}>Item #{item.itemNumber}</span>
                )}
              </div>
              {/* Meta: title + description */}
              <div style={s.cardInfo}>
                <span style={s.cardName}>{item?.name}</span>
                {item?.description && <span style={s.cardDesc}>{item.description}</span>}
              </div>
              {/* Price column */}
              <div style={s.cardRight}>
                {meta?.auction && (
                  <>
                    <span style={s.price}>${Math.ceil(meta.auction.currentPrice)}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: meta.auction.millisecondsRemaining <= 10000 ? colors.accent.pink : colors.gray[700] }}>
                      {(meta.auction.millisecondsRemaining / 1000).toFixed(1)}s
                    </span>
                    {meta.auction.suddenDeathEnabled && (
                      <span style={s.urgentTag}>Sudden death</span>
                    )}
                  </>
                )}
                {meta?.ended && (
                  <>
                    <span style={s.price}>{meta.ended.winnerId ? `$${Math.ceil(meta.ended.winningBid || 0)}` : 'No sale'}</span>
                  </>
                )}
              </div>
            </div>
            {/* Pill + status strip row — hidden when shrunk */}
            {!isShrink && (
              <div style={s.pillStripRow}>
                <div style={s.pillRow}>
                  {useBalance && availableBalance > 0 && (
                    <span style={s.pillCredit}>
                      <Wallet size={10} /> ${Math.floor(availableBalance)} credit
                    </span>
                  )}
                  {meta?.estimate && (
                    <span style={s.pillShip}>
                      {meta.estimate.amount === 0 ? 'Free shipping + tax' : `${formatShippingCost(meta.estimate.amount)} shipping + tax`}
                    </span>
                  )}
                </div>
                {statusStrip}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBidBar = () => {
    if (!activeAuction) {
      if (auctionEnded) {
        if (auctionEnded.winnerId === user?.id) {
          return <div style={{ ...s.barFull, background: colors.brand.blue }}>You won for ${Math.ceil(auctionEnded.winningBid || 0)}!</div>;
        }
        if (auctionEnded.winnerId) {
          return <div style={{ ...s.barFull, background: '#6B6F85' }}>{auctionEnded.winnerUsername || 'Winner'} won for ${Math.ceil(auctionEnded.winningBid || 0)}</div>;
        }
        return <div style={{ ...s.barFull, background: '#6B6F85' }}>Auction ended — No winner</div>;
      }
      return null;
    }

    // Wallet not set up
    if (buyerValidation.missingPaymentMethod) {
      return (
        <Link to="/payment-methods" style={{ ...s.barFull, background: colors.brand.blue, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <CreditCard size={15} /> Add a card to bid
        </Link>
      );
    }

    // Payment failed
    if (buyerValidation.hasFailedPayment) {
      return (
        <button onClick={() => setShowRetryModal(true)} style={{ ...s.barFull, background: colors.accent.pink, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <AlertCircle size={15} /> Fix failed payment to bid
        </button>
      );
    }

    if (activeAuction.millisecondsRemaining <= 0) {
      return <div style={{ ...s.barFull, background: '#6B6F85', color: 'rgba(255,255,255,0.92)' }}>Auction ending...</div>;
    }

    // You're winning — two-part with green status bar
    if (activeAuction.highestBidderId === user?.id) {
      return (
        <div style={s.barRow}>
          <button onClick={handleCustomBid} style={s.barCustom}>Custom</button>
          <div style={{ ...s.barBidSlot, background: '#16A34A' }}>
            <Check size={15} strokeWidth={2.5} style={{ marginRight: 6 }} />
            You're winning
            {userMaxBidAmount != null && (
              <span style={s.maxPill}>Max ${Math.ceil(userMaxBidAmount)}</span>
            )}
          </div>
        </div>
      );
    }

    // Bid pending
    if (bidStatus === 'pending') {
      return (
        <div style={s.barRow}>
          <button onClick={handleCustomBid} style={s.barCustom}>Custom</button>
          <div style={{ ...s.barBidSlot, background: '#6B6F85', color: 'rgba(255,255,255,0.92)' }}>
            <span className="bv-spinner" style={{ marginRight: 8 }} />
            Bid pending...
          </div>
        </div>
      );
    }

    // Active bidding — two-part layout
    const isUrgent = activeAuction.millisecondsRemaining <= 10000;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={s.barRow}>
          {canBid && <button onClick={handleCustomBid} style={s.barCustom}>Custom</button>}
          {!canBid && buyerValidation.missingVerification ? (
            <button onClick={showVerification}
              style={{ ...s.barBidBtn, flex: 1, background: colors.brand.blue, cursor: 'pointer' }}>
              <span>Verify Account to Bid</span>
              <span style={{ fontSize: 13, opacity: 0.9, lineHeight: 1 }}>&rsaquo;</span>
            </button>
          ) : (
            <button onClick={handlePlaceBid} disabled={!canBid}
              style={{ ...s.barBidBtn, background: isUrgent ? colors.accent.pink : colors.brand.blue, cursor: canBid ? 'pointer' : 'not-allowed', opacity: canBid ? 1 : 0.6 }}>
              <span>{!canBid ? (buyerValidation.missingAddress ? 'Add Address' : 'Add Card') : `Bid $${nextBidAmount}`}</span>
              <span style={{ fontSize: 13, opacity: 0.9, lineHeight: 1 }}>&rsaquo;</span>
            </button>
          )}
        </div>
        {bidStatus === 'rejected' && <span style={{ color: '#DC2626', fontSize: 11 }}>{isMissingPaymentMethodRejection ? 'Payment method required' : isMissingAddressRejection ? 'Address required' : bidRejectionReason || 'Bid rejected'}</span>}
      </div>
    );
  };

  const renderBinSale = () => {
    if (!activeBinSale?.item) return null;
    const binItem = activeBinSale.item;
    return (
      <>
        <div style={s.card}>
          <div style={s.cardRow}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {binItem.imageUrl ? <Img src={binItem.imageUrl} alt={binItem.name} variant="thumb" renderedWidth={64} blurhash={binItem.blurhash} style={s.cardImg} /> : <div style={s.cardImgPlaceholder}><Package size={20} color="#aaa" /></div>}
              {binItem.itemNumber != null && (
                <span style={s.thumbBadge}>Item #{binItem.itemNumber}</span>
              )}
            </div>
            <div style={s.cardInfo}>
              <span style={s.cardName}>{binItem.name}</span>
              {binItem.description && <span style={s.cardDesc}>{binItem.description}</span>}
            </div>
            <div style={s.cardRight}>
              <span style={s.price}>${activeBinSale.finalPrice.toFixed(2)}</span>
              {activeBinSale.saleEnabled && activeBinSale.basePrice > activeBinSale.finalPrice && <span style={{ fontSize: 12, color: colors.gray[400], textDecoration: 'line-through', lineHeight: 1 }}>${activeBinSale.basePrice.toFixed(2)}</span>}
              {activeBinSale.saleEnabled && <span style={{ fontSize: 6.5, fontWeight: 700, color: '#fff', background: colors.accent.pink, padding: '1px 4px', borderRadius: 3, textTransform: 'uppercase' as const, letterSpacing: '0.08em', marginTop: 1 }}>{activeBinSale.saleType === 'PERCENT' ? `${activeBinSale.saleAmount}% OFF` : `$${activeBinSale.saleAmount} OFF`}</span>}
            </div>
          </div>
          {/* Pill + status strip row */}
          <div style={s.pillStripRow}>
            <div style={s.pillRow}>
              {useBalance && availableBalance > 0 && (
                <span style={s.pillCredit}>
                  <Wallet size={10} /> ${Math.floor(availableBalance)} credit
                </span>
              )}
              {binShippingEstimate && (
                <span style={s.pillShip}>
                  {binShippingEstimate.amount === 0 ? 'Free shipping + tax' : `${formatShippingCost(binShippingEstimate.amount)} shipping + tax`}
                </span>
              )}
            </div>
            <div style={{ ...s.statusStrip, color: colors.brand.blue }}>
              <b>BUY IT NOW</b>
              {activeBinSale.remainingQuantity > 1 && <span style={{ fontWeight: 500, opacity: 0.85 }}>&middot; {activeBinSale.remainingQuantity} left</span>}
            </div>
          </div>
        </div>
        {activeBinSale.remainingQuantity > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {!canBid && buyerValidation.missingVerification ? (
              <button onClick={showVerification}
                style={{ ...s.barFull, background: colors.brand.blue, cursor: 'pointer', border: 'none' }}>
                Verify Account to Buy
              </button>
            ) : (
              <button onClick={handleBuyNow} disabled={binPurchaseStatus === 'pending' || !canBid}
                style={{ ...s.barFull, background: canBid ? colors.accent.pink : colors.gray[400], cursor: canBid ? 'pointer' : 'not-allowed', border: 'none' }}>
                {binPurchaseStatus === 'pending' ? 'Processing...' : !canBid ? (buyerValidation.missingPaymentMethod ? 'Add Card to Buy' : 'Add Address to Buy') : `Buy Now — $${activeBinSale.finalPrice.toFixed(2)}`}
              </button>
            )}
            {binPurchaseStatus === 'success' && <span style={{ color: '#22C55E', fontSize: 11, fontWeight: 600, textAlign: 'center' }}>Purchased!</span>}
            {binPurchaseStatus === 'failed' && <span style={{ color: '#DC2626', fontSize: 11, textAlign: 'center' }}>{binPurchaseFailReason || 'Failed'}</span>}
          </div>
        ) : <div style={{ ...s.barFull, background: colors.gray[600] }}>Sold Out</div>}
      </>
    );
  };

  const renderOnDeck = () => {
    if (!onDeckItem || activeAuction || auctionEnded || activeBinSale) return null;
    return (
      <>
        <div style={s.cardShrink}>
          <div style={s.cardRow}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {onDeckItem.imageUrl ? <Img src={onDeckItem.imageUrl} alt={onDeckItem.name} variant="thumb" renderedWidth={64} blurhash={onDeckItem.blurhash} style={s.cardImg} /> : <div style={s.cardImgPlaceholder}><Package size={20} color="#aaa" /></div>}
              {onDeckItem.itemNumber != null && (
                <span style={s.thumbBadge}>Item #{onDeckItem.itemNumber}</span>
              )}
            </div>
            <div style={s.cardInfo}>
              <span style={s.cardName}>{onDeckItem.name}</span>
              {onDeckItem.description && <span style={s.cardDesc}>{onDeckItem.description}</span>}
            </div>
            <div style={s.cardRight}>
              <span style={{ fontSize: 12, fontWeight: 600, color: colors.gray[600] }}>Starting...</span>
            </div>
          </div>
        </div>
        <div style={{ ...s.barFull, background: 'rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.18)', fontWeight: 600 }}>Auction starting soon...</div>
      </>
    );
  };

  // ---- Main render ----
  return (
    <HMSRoomProvider>
      <div className="bv-container">
        <Header />

        <main className="bv-main">
          {/* Left column: Video + Auction */}
          <div className="bv-left">
            <div className="bv-video">
              <VideoViewer authToken={authToken} userName={user?.username || 'Viewer'} thumbnailUrl={show?.thumbnailUrl} broadcasterPaused={broadcasterPaused} />

              {celebration && (
                <AuctionWinOverlay
                  username={celebration.username}
                  avatarUrl={
                    celebration.winnerId === user?.id
                      ? user?.avatarUrl ?? null
                      : winnerAvatarUrl
                  }
                  onComplete={() => setCelebration(null)}
                />
              )}

              {/* Top overlay: seller + viewer count + share */}
              <div className="bv-video-top-overlay">
                {show?.sellerUsername && show?.sellerId && (
                  <SellerMenu
                    sellerId={show.sellerId}
                    sellerUsername={show.sellerUsername}
                    sellerAvatarUrl={show.sellerAvatarUrl}
                    currentUserId={user?.id}
                  />
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  {isConnected && <div className="bv-viewer-count"><Eye size={14} /> {viewerCount}</div>}
                </div>
              </div>

              {/* Banners: full width, below seller bar, above side menu */}
              <div style={{ position: 'absolute', top: 52, left: 0, right: 0, zIndex: 10, padding: '0 12px' }}>
                {renderBanners()}
              </div>

              {/* Side action menu */}
              <div className="bv-side-menu" style={{ top: `calc(${64 + bannerCount * 48}px + env(safe-area-inset-top, 0px))` }}>
                <button type="button" className="bv-side-menu-item" aria-label={isMuted ? 'Unmute' : 'Mute'} onClick={toggleMute}>
                  {isMuted ? <VolumeX size={22} strokeWidth={1.8} /> : <Volume2 size={22} strokeWidth={1.8} />}
                  <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                </button>
                <button type="button" className="bv-side-menu-item" aria-label="Notes" onClick={() => setSideMenuOpen('notes')}>
                  <FileText size={22} strokeWidth={1.8} />
                  <span>Notes</span>
                </button>
                <button type="button" className="bv-side-menu-item" aria-label="Wallet" onClick={() => setSideMenuOpen('wallet')}>
                  <Wallet size={22} strokeWidth={1.8} />
                  <span>Wallet</span>
                </button>
                <button type="button" className="bv-side-menu-item" aria-label="Purchases" onClick={() => setSideMenuOpen('purchases')}>
                  <ShoppingBag size={22} strokeWidth={1.8} />
                  <span>Purchases</span>
                </button>
                <button type="button" className="bv-side-menu-item" aria-label="Share" onClick={() => setShowShareModal(true)}>
                  <Share2 size={22} strokeWidth={1.8} />
                  <span>Share</span>
                </button>
              </div>

              {/* Bottom overlay: chat (mobile) + banners + auction controls */}
              <div className="bv-video-bottom-overlay">
                {/* Chat overlaid on mobile — hidden on desktop via CSS */}
                <div className="bv-mobile-chat">
                  <Chat showId={show?.id} channelId={show?.chatChannelId ?? null} sellerId={show?.sellerId} maxHeight={200} />
                </div>
                <div className="bv-auction-area">
                  {(activeAuction || auctionEnded) && (currentItem || onDeckItem) && renderItemCard(currentItem || onDeckItem, { auction: activeAuction ?? undefined, ended: auctionEnded ?? undefined, estimate: shippingEstimate ?? undefined })}
                  {renderBidBar()}
                  {renderBinSale()}
                  {binSaleEnded && !activeBinSale && <div style={{ ...s.barFull, background: '#6B6F85' }}>Sale ended — {binSaleEnded.totalSold} sold</div>}
                  {renderOnDeck()}
                  {!onDeckItem && !activeAuction && !auctionEnded && !activeBinSale && (
                    <div style={{ ...s.waitingCard, background: 'rgba(255,255,255,0.85)', border: 'none' }}>Waiting for the next item...</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right column: Chat + Info */}
          <div className="bv-right">

            <div className="bv-chat-wrapper">
              <Chat showId={show?.id} channelId={show?.chatChannelId ?? null} sellerId={show?.sellerId} maxHeight={9999} />
            </div>

            <div className="bv-info-section">
            </div>
          </div>
        </main>
        <footer className="bv-footer">
          &copy; {new Date().getFullYear()} Snagg &middot; <a href="https://snagg.live/terms" style={{ color: '#999' }}>Terms</a> &middot; <a href="https://snagg.live/privacy" style={{ color: '#999' }}>Privacy</a>
        </footer>
      </div>

      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} url={shareUrl} title={shareTitle} />
      {showRetryModal && failedSale && (
        <RetryPaymentModal saleId={failedSale.id} itemName={failedSale.itemName} amount={failedSale.amount}
          onClose={() => setShowRetryModal(false)} onSuccess={() => { setShowRetryModal(false); setFailedSale(null); refreshValidation(); }} />
      )}
      {sideMenuOpen === 'notes' && (
        <NotesModal
          shippingConfig={shippingConfig}
          sellerNotes={show?.sellerNotes}
          onClose={() => setSideMenuOpen(null)}
        />
      )}
      {sideMenuOpen === 'wallet' && (
        <WalletModal onClose={() => setSideMenuOpen(null)} useBalance={useBalance} onUseBalanceChange={handleUseBalanceChange} />
      )}
      {showCustomBidModal && activeAuction && (
        <CustomBidModal
          currentPrice={activeAuction.currentPrice}
          minimumBid={nextBidAmount}
          userMaxBidAmount={userMaxBidAmount}
          onPlaceBid={handleCustomBidSubmit}
          onClose={() => setShowCustomBidModal(false)}
        />
      )}
      {sideMenuOpen === 'purchases' && show?.id && (
        <PurchasesModal
          showId={show.id}
          onClose={() => setSideMenuOpen(null)}
          onPaymentFixed={() => refreshValidation()}
        />
      )}
    </HMSRoomProvider>
  );
}

// ---- Unauth preview component (fetches its own viewer token) ----
function UnauthPreview({ show, showId, isMobile, shareUrl, shareTitle, showShareModal, setShowShareModal, showAuthModal, setShowAuthModal, onAuthenticated }: {
  show: Show;
  showId: string;
  isMobile: boolean;
  shareUrl: string;
  shareTitle: string;
  showShareModal: boolean;
  setShowShareModal: (v: boolean) => void;
  showAuthModal: boolean;
  setShowAuthModal: (v: boolean) => void;
  onAuthenticated: () => void;
}) {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [appSheetCollapsed, setAppSheetCollapsed] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const peerCount = useHMSStore(selectPeerCount);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      const audioEl = document.querySelector<HTMLAudioElement>('audio');
      if (audioEl) {
        audioEl.volume = next ? 0 : 0.8;
        if (!next && audioEl.paused) {
          audioEl.play().catch(() => {});
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (show.status === 'LIVE' && show.hmsRoomId) {
      getPublicHmsViewerToken(showId).then(t => setAuthToken(t.authToken)).catch(() => {});
    }
  }, [showId, show.status, show.hmsRoomId]);

  // On desktop, show auth modal immediately; on mobile, don't
  useEffect(() => {
    if (!isMobile) setShowAuthModal(true);
  }, [isMobile, setShowAuthModal]);

  // Detect platform for store badge
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const appStoreUrl = 'https://apps.apple.com/app/snagg/id6744594498';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=live.snagg';

  // ---- Mobile: app install bottom sheet ----
  if (isMobile) {
    return (
      <>
        <div style={{ position: 'fixed', top: 'calc(-1 * env(safe-area-inset-top, 0px))', left: 0, right: 0, bottom: 0, backgroundColor: '#000', zIndex: 0 }}>
          {/* Video — fills entire screen including behind status bar */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <VideoViewer authToken={authToken} userName="Guest" thumbnailUrl={show?.thumbnailUrl} broadcasterPaused={false} />
          </div>

          {/* Top overlay: seller avatar + username + viewer count */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `calc(12px + env(safe-area-inset-top, 0px)) 14px 12px`, background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 100%)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UserAvatar
                src={show.sellerAvatarUrl}
                blurhash={show.sellerAvatarBlurhash}
                alt={show.sellerUsername ?? 'Seller'}
                size={48}
                style={{ flexShrink: 0 }}
              />
              <span style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>{show.sellerUsername}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={toggleMute} style={{ background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: 20, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#fff' }}>
                {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#fff', background: 'rgba(0,0,0,0.4)', padding: '6px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                <Eye size={14} /> {peerCount || 1}
              </div>
            </div>
          </div>

          {/* Bottom: gradient fade into sheet */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.4))', zIndex: 5, pointerEvents: 'none' }} />
        </div>

          {/* App install bottom sheet */}
          {!appSheetCollapsed ? (
            <div style={unauthMobileStyles.sheet}>
              {/* Tight cluster: logo + CTA + badge */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                <img src="/img/snagg_assets/snagg_combined_color.svg" alt="Snagg" style={{ height: 24 }} />
              </div>
              <p style={{ fontSize: 16, fontWeight: 600, color: colors.ink.black, margin: '0 0 14px', lineHeight: 1.4, textAlign: 'center' }}>
                Get the app to bid, chat, and never miss a deal
              </p>
              <div>
                {isIOS ? (
                  <a href={appStoreUrl} style={{ display: 'flex', justifyContent: 'center' }}>
                    <img src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83" alt="Download on the App Store" style={{ height: 44 }} />
                  </a>
                ) : (
                  <a href={playStoreUrl} style={unauthMobileStyles.storeBadge}>
                    <svg width="20" height="22" viewBox="0 0 24 24" fill="white"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-1.35l2.473 1.432c.466.27.466.943 0 1.212l-2.473 1.432L15.13 12l2.568-2.643zM5.864 1.469L16.8 7.802l-2.302 2.302-8.635-8.635z"/></svg>
                    <div>
                      <div style={{ fontSize: 11, opacity: 0.8 }}>Get it on</div>
                      <div style={{ fontSize: 17, fontWeight: 600 }}>Google Play</div>
                    </div>
                  </a>
                )}
              </div>

              {/* Separate action — more breathing room */}
              <button onClick={() => setAppSheetCollapsed(true)} style={unauthMobileStyles.continueWatching}>
                Continue watching
              </button>
            </div>
          ) : (
            /* Collapsed mini banner */
            <a href={isIOS ? appStoreUrl : playStoreUrl} style={unauthMobileStyles.miniBanner}>
              <img src="/img/snagg_assets/snagg_logo-square.svg" alt="" style={{ width: 28, height: 28, borderRadius: 6 }} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: colors.ink.black }}>Get the app to bid</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', backgroundColor: colors.brand.blue, padding: '6px 14px', borderRadius: 16 }}>Download</span>
            </a>
          )}
        <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} url={shareUrl} title={shareTitle} />
      </>
    );
  }

  // ---- Desktop: auth modal over live stream + chat ----
  return (
    <>
      <div className="bv-container">
        <Header />
        <main className="bv-main">
          <div className="bv-left">
            <div className="bv-video">
              <VideoViewer authToken={authToken} userName="Guest" thumbnailUrl={show?.thumbnailUrl} broadcasterPaused={false} />

              <div className="bv-video-top-overlay">
                <div className="bv-seller-bar" style={{ textDecoration: 'none' }}>
                  <UserAvatar src={show.sellerAvatarUrl} blurhash={show.sellerAvatarBlurhash} alt={show.sellerUsername ?? 'Seller'} size={40} />
                  <span className="bv-seller-name">{show.sellerUsername}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="bv-share-btn" onClick={toggleMute}>{isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />} {isMuted ? 'Unmute' : 'Mute'}</button>
                  <button className="bv-share-btn" onClick={() => setShowShareModal(true)}><Share2 size={14} /> Share</button>
                </div>
              </div>

              <div className="bv-video-bottom-overlay" />
            </div>
          </div>

          <div className="bv-right">
            <div className="bv-chat-wrapper">
              <ReadOnlyChat channelId={show.chatChannelId ?? null} sellerId={show.sellerId} maxHeight={9999} onSignInClick={() => setShowAuthModal(true)} />
            </div>
          </div>
        </main>
        <footer className="bv-footer">
          &copy; {new Date().getFullYear()} Snagg &middot; <a href="https://snagg.live/terms" style={{ color: '#999' }}>Terms</a> &middot; <a href="https://snagg.live/privacy" style={{ color: '#999' }}>Privacy</a>
        </footer>
      </div>

      <ShareModal isOpen={showShareModal} onClose={() => setShowShareModal(false)} url={shareUrl} title={shareTitle} />
      <AuthPromptModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthenticated={onAuthenticated}
        sellerAvatarUrl={show.sellerAvatarUrl}
        sellerUsername={show.sellerUsername}
        transparent
      />
    </>
  );
}

const unauthMobileStyles: Record<string, React.CSSProperties> = {
  sheet: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: '20px 20px 0 0',
    padding: '16px 24px calc(12px + env(safe-area-inset-bottom, 0px))',
    zIndex: 100,
    boxShadow: '0 -4px 24px rgba(0,0,0,0.15)',
  },
  storeBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    padding: '14px 0',
    backgroundColor: '#000',
    color: '#fff',
    borderRadius: 12,
    textDecoration: 'none',
    boxSizing: 'border-box',
  },
  continueWatching: {
    display: 'block',
    width: '100%',
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: 14,
    cursor: 'pointer',
    padding: '20px 0 0',
    marginTop: 4,
    textAlign: 'center',
  },
  miniBanner: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px calc(10px + env(safe-area-inset-bottom, 0px))',
    backgroundColor: '#fff',
    borderTop: '1px solid #e5e5ea',
    zIndex: 100,
    textDecoration: 'none',
    color: 'inherit',
  },
};

// Compact inline styles (layout handled by CSS classes)
const s: Record<string, React.CSSProperties> = {
  container: { backgroundColor: colors.gray[100], color: colors.ink.black },
  loadingText: { textAlign: 'center', color: '#666', marginTop: 100 },
  centeredState: { textAlign: 'center', marginTop: 100 },
  stateText: { fontSize: 18, color: '#666', marginBottom: 16 },
  grayBtn: { padding: '12px 24px', backgroundColor: colors.gray[300], color: colors.ink.black, border: 'none', borderRadius: 8, cursor: 'pointer' },
  primaryBtn: { padding: '12px 24px', backgroundColor: '#007AFF', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover' },
  previewOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' },
  playButton: { width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginBottom: 12 },
  playIcon: { fontSize: 28, color: '#fff', marginLeft: 4 },
  previewText: { color: '#fff', fontSize: 16, fontWeight: 600 },
  card: { background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', borderRadius: 16, padding: '11px 12px', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', flexDirection: 'column', gap: 4 },
  cardShrink: { background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)', borderRadius: 16, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', flexDirection: 'column', gap: 4 },
  cardRow: { display: 'flex', alignItems: 'stretch', gap: 11 },
  cardImg: { width: 52, height: 52, objectFit: 'cover', borderRadius: 10, flexShrink: 0 },
  cardImgPlaceholder: { width: 52, height: 52, borderRadius: 10, background: '#f2f2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  thumbBadge: { position: 'absolute', left: '50%', bottom: -6, transform: 'translateX(-50%)', background: '#fff', color: '#1C2033', border: '1.5px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', borderRadius: 9999, fontSize: 9, fontWeight: 700, padding: '1px 6px', lineHeight: 1.4, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  cardInfo: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, gap: 2 },
  cardName: { fontSize: 13, fontWeight: 700, color: colors.ink.black, letterSpacing: '-0.01em', lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' } as React.CSSProperties,
  cardDesc: { fontSize: 11, color: '#757783', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' } as React.CSSProperties,
  cardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 2, minWidth: 48, flexShrink: 0 },
  price: { fontSize: 18, fontWeight: 800, color: colors.ink.black, letterSpacing: '-0.02em', lineHeight: 1 },
  urgentTag: { background: '#FF2D55', color: '#fff', fontSize: 6.5, fontWeight: 700, letterSpacing: '0.08em', padding: '1px 4px', borderRadius: 3, textTransform: 'uppercase' as const, lineHeight: 1.4, alignSelf: 'flex-end', marginTop: 1 },
  pillStripRow: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, minWidth: 0 },
  pillRow: { display: 'flex', gap: 6, flexWrap: 'nowrap', overflow: 'hidden', flex: '1 1 auto', minWidth: 0 },
  pillShip: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '2px 7px', borderRadius: 9999,
    fontSize: 10, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'nowrap', flexShrink: 0,
    background: '#DCFCE7', color: '#15803D',
  },
  pillCredit: {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '2px 7px', borderRadius: 9999,
    fontSize: 10, fontWeight: 600, lineHeight: 1.4, whiteSpace: 'nowrap', flexShrink: 0,
    background: 'rgba(95,61,196,0.13)', color: '#5F3DC4',
  },
  statusStrip: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap', flex: '0 0 auto' },
  statusDot: { width: 7, height: 7, borderRadius: 9999, flexShrink: 0 },
  // Action bar styles
  barRow: { display: 'flex', gap: 8 },
  barCustom: { flex: '0 0 88px', background: '#fff', borderRadius: 12, padding: '12px 9px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#1C2033', cursor: 'pointer', border: 'none' },
  barBidSlot: { flex: 1, color: '#fff', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 },
  barBidBtn: { flex: 1, color: '#fff', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' },
  barFull: { borderRadius: 12, padding: '12px 14px', textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 },
  waitingCard: { background: '#fff', borderRadius: 12, padding: 32, border: `1px solid ${colors.gray[300]}`, textAlign: 'center', color: '#666' },
  maxPill: { display: 'inline-flex', alignItems: 'center', gap: 3, background: 'rgba(255,255,255,0.22)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 9999, marginLeft: 8 },
  bidFlash: { position: 'absolute' as const, right: 0, bottom: '100%', marginBottom: 1, fontSize: 10, fontWeight: 600, color: '#757783', whiteSpace: 'nowrap' as const, animation: 'bv-bid-flash 2.6s ease-out forwards' },
};
